import { z } from "zod";
import { imageRefSchema } from "./catalog";
import { membershipStatusSchema, restaurantStatusSchema, staffRoleSchema } from "./enums";
import { PERMISSIONS } from "./permissions";
import { entitlementsSchema } from "./platform-billing";
import {
	accentColorSchema,
	openingHoursSchema,
	pincodeSchema,
	restaurantSettingsSchema,
	stateCodeSchema,
} from "./restaurant";
import { slugSchema } from "./slug";

/** Restaurant input and output shapes shared by the API and its clients. */

const text = (max: number) => z.string().trim().min(1).max(max);
/** An optional text field. An empty string clears it. */
const optionalText = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.transform((v) => (v === "" ? null : v))
		.nullable();

/** A phone number as typed: digits, spaces, hyphens and a leading +. */
export const phoneSchema = z
	.string()
	.trim()
	.regex(/^\+?[0-9][0-9 -]{5,18}[0-9]$/, "A phone number");

/** An IANA time zone the runtime knows, such as Asia/Kolkata. */
export const timeZoneSchema = z.string().refine(
	(tz) => {
		try {
			new Intl.DateTimeFormat("en", { timeZone: tz });
			return true;
		} catch {
			return false;
		}
	},
	{ message: "Unknown time zone" },
);

/** The editable facts about a restaurant. Every field optional, for partial updates. */
export const restaurantProfileSchema = z.object({
	name: text(80),
	legalName: optionalText(120),
	phone: phoneSchema.nullable().or(z.literal("").transform(() => null)),
	email: z
		.email()
		.max(254)
		.nullable()
		.or(z.literal("").transform(() => null)),
	addressLine1: optionalText(120),
	addressLine2: optionalText(120),
	city: optionalText(60),
	stateCode: stateCodeSchema.nullable().or(z.literal("").transform(() => null)),
	pincode: pincodeSchema.nullable().or(z.literal("").transform(() => null)),
	latitude: z.number().min(-90).max(90).nullable(),
	longitude: z.number().min(-180).max(180).nullable(),
	timezone: timeZoneSchema,
	cuisines: z.array(text(40)).max(10),
	hours: openingHoursSchema,
});
export type RestaurantProfile = z.infer<typeof restaurantProfileSchema>;

/** At most this many gallery photos (the website's gallery section). */
export const MAX_GALLERY_PHOTOS = 12;

/**
 * Logo and cover: uploaded files of purpose `logo` and `cover`, or null to
 * remove. The gallery: the whole ordered list of `gallery` files (P7-T16).
 */
export const restaurantImagesPatchSchema = z.object({
	logoFileId: z.uuid().nullable().optional(),
	coverFileId: z.uuid().nullable().optional(),
	galleryFileIds: z
		.array(z.uuid())
		.max(MAX_GALLERY_PHOTOS)
		.refine((ids) => new Set(ids).size === ids.length, "Each photo once")
		.optional(),
});

export const restaurantProfilePatchSchema = restaurantProfileSchema
	.partial()
	.extend(restaurantImagesPatchSchema.shape);
export type RestaurantProfilePatch = z.infer<typeof restaurantProfilePatchSchema>;

export const createRestaurantInputSchema = restaurantProfileSchema.partial().extend({
	slug: slugSchema,
	name: text(80),
});
export type CreateRestaurantInput = z.infer<typeof createRestaurantInputSchema>;

export const restaurantSchema = z.object({
	id: z.uuid(),
	slug: z.string(),
	name: z.string(),
	legalName: z.string().nullable(),
	status: restaurantStatusSchema,
	phone: z.string().nullable(),
	email: z.string().nullable(),
	addressLine1: z.string().nullable(),
	addressLine2: z.string().nullable(),
	city: z.string().nullable(),
	stateCode: z.string().nullable(),
	pincode: z.string().nullable(),
	latitude: z.number().nullable(),
	longitude: z.number().nullable(),
	timezone: z.string(),
	logoFileId: z.uuid().nullable(),
	coverFileId: z.uuid().nullable(),
	/** The website's gallery photos, in order. */
	galleryFileIds: z.array(z.uuid()),
	cuisines: z.array(z.string()),
	hours: openingHoursSchema,
	settings: restaurantSettingsSchema,
	createdAt: z.iso.datetime(),
});
export type Restaurant = z.infer<typeof restaurantSchema>;

/**
 * Status changes. A restaurant never returns to `onboarding`. Archived
 * restaurants can be restored to active.
 */
export const RESTAURANT_STATUS_TRANSITIONS = {
	onboarding: ["active", "suspended", "archived"],
	active: ["suspended", "archived"],
	suspended: ["active", "archived"],
	archived: ["active"],
} as const satisfies Record<
	z.infer<typeof restaurantStatusSchema>,
	readonly z.infer<typeof restaurantStatusSchema>[]
>;

export function canChangeStatus(
	from: z.infer<typeof restaurantStatusSchema>,
	to: z.infer<typeof restaurantStatusSchema>,
): boolean {
	return (RESTAURANT_STATUS_TRANSITIONS[from] as readonly string[]).includes(to);
}

/** A restaurant as a member sees it in the chooser. */
export const myRestaurantSchema = z.object({
	id: z.uuid(),
	slug: z.string(),
	name: z.string(),
	status: restaurantStatusSchema,
	logoFileId: z.uuid().nullable(),
	role: staffRoleSchema,
});
export type MyRestaurant = z.infer<typeof myRestaurantSchema>;

/** `staff.tenancy.getCurrent`: the active restaurant and what the caller may do there. */
export const currentMembershipSchema = z.object({
	restaurant: restaurantSchema,
	role: staffRoleSchema,
	permissions: z.array(z.enum(PERMISSIONS)),
	/** A superadmin support session: the app shows a banner. */
	supportSession: z.boolean(),
	/**
	 * The plan's features and limits and the subscription's status (P10-T04):
	 * the apps hide what the plan does not include. The server checks anyway.
	 */
	entitlements: entitlementsSchema,
});
export type CurrentMembership = z.infer<typeof currentMembershipSchema>;

/** A row in More > Staff. */
export const memberSchema = z.object({
	/** The membership ID. */
	id: z.uuid(),
	userId: z.uuid(),
	name: z.string().nullable(),
	email: z.string().nullable(),
	avatarUrl: z.string().nullable(),
	role: staffRoleSchema,
	status: membershipStatusSchema,
	joinedAt: z.iso.datetime(),
});
export type Member = z.infer<typeof memberSchema>;

/** Change a member's role, status or both. */
export const updateMemberInputSchema = z
	.object({
		membershipId: z.uuid(),
		role: staffRoleSchema.optional(),
		status: membershipStatusSchema.optional(),
	})
	.refine((v) => v.role !== undefined || v.status !== undefined, {
		message: "Nothing to change",
	});
export type UpdateMemberInput = z.infer<typeof updateMemberInputSchema>;

/** `admin.tenancy.listRestaurants` */
export const listRestaurantsInputSchema = z.object({
	/** Matches name, slug or city, ignoring case. */
	search: z.string().trim().max(80).optional(),
	status: restaurantStatusSchema.optional(),
	/** The last ID of the previous page. Newest first. */
	cursor: z.uuid().optional(),
	limit: z.number().int().min(1).max(100).default(50),
});
export type ListRestaurantsInput = z.input<typeof listRestaurantsInputSchema>;

export const restaurantListItemSchema = restaurantSchema.pick({
	id: true,
	slug: true,
	name: true,
	status: true,
	city: true,
	phone: true,
	createdAt: true,
});
export type RestaurantListItem = z.infer<typeof restaurantListItemSchema>;

export const restaurantPageSchema = z.object({
	items: z.array(restaurantListItemSchema),
	nextCursor: z.uuid().nullable(),
});

/** `admin.tenancy.updateRestaurant`. The slug can change only in onboarding. */
export const adminUpdateRestaurantInputSchema = restaurantProfilePatchSchema.extend({
	restaurantId: z.uuid(),
	slug: slugSchema.optional(),
});
export type AdminUpdateRestaurantInput = z.infer<typeof adminUpdateRestaurantInputSchema>;

export const setRestaurantStatusInputSchema = z.object({
	restaurantId: z.uuid(),
	status: restaurantStatusSchema,
	reason: z.string().trim().max(200).optional(),
});

/** Owner invites from the superadmin are always bound to an email. */
export const inviteOwnerInputSchema = z.object({
	restaurantId: z.uuid(),
	email: z
		.email()
		.max(254)
		.transform((e) => e.toLowerCase()),
	expiresInDays: z.number().int().min(1).max(30).default(7),
});

/** `admin.tenancy.impersonate` */
export const impersonateInputSchema = z.object({
	restaurantId: z.uuid(),
	/** Why support needs to look. Written to the audit log. */
	reason: z.string().trim().min(5).max(200),
});

export const supportSessionSchema = z.object({
	token: z.string(),
	expiresAt: z.iso.datetime(),
	/** Opens staff web as the restaurant. The token is in the fragment. */
	url: z.url(),
});
export type SupportSession = z.infer<typeof supportSessionSchema>;

/**
 * `public.tenancy.getRestaurantByHost`: what the storefront shows about the
 * restaurant of the host: header, home page, footer (FSSAI, BL-09) and which
 * ordering features are on. Archived restaurants are `NOT_FOUND`.
 */
/** An old web address keeps pointing to its restaurant this long after a change (P7-T20). */
export const SLUG_REDIRECT_DAYS = 90;

/**
 * `public.tenancy.getSlugRedirect`: the restaurant an old web address now
 * belongs to, for the storefront to redirect to its current address.
 */
export const slugRedirectSchema = z.object({
	slug: z.string(),
	restaurantId: z.uuid(),
});
export type SlugRedirect = z.infer<typeof slugRedirectSchema>;

export const publicRestaurantSchema = z.object({
	id: z.uuid(),
	slug: z.string(),
	name: z.string(),
	/** `suspended`: browse only. `onboarding`: not launched yet, shown to its owner's testers. */
	status: z.enum(["onboarding", "active", "suspended"]),
	logo: imageRefSchema.nullable(),
	cover: imageRefSchema.nullable(),
	phone: z.string().nullable(),
	email: z.string().nullable(),
	addressLine1: z.string().nullable(),
	addressLine2: z.string().nullable(),
	city: z.string().nullable(),
	stateCode: z.string().nullable(),
	pincode: z.string().nullable(),
	latitude: z.number().nullable(),
	longitude: z.number().nullable(),
	timezone: z.string(),
	cuisines: z.array(z.string()),
	hours: openingHoursSchema,
	/** The FSSAI licence number for the menu footer. */
	fssaiNo: z.string().nullable(),
	/** The owner's settings, all off while the restaurant is suspended. */
	ordering: z.object({
		qr: z.boolean(),
		takeaway: z.boolean(),
		serviceRequests: z.boolean(),
		askGuestName: z.boolean(),
	}),
	/** Menu languages, first is the default. */
	languages: z.array(z.string()),
	/** The storefront's accent colour, applied as CSS variables. */
	accent: accentColorSchema,
	/** Show "Representative image" under photos marked `ai` (P8-T20). */
	aiImageNote: z.boolean(),
});
export type PublicRestaurant = z.infer<typeof publicRestaurantSchema>;
